import React, { useCallback, useContext, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Heading from 'LuxKit/Typography/Heading'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import Divider from 'LuxKit/Divider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import LePackageUpgradePrice from './LePackageUpgradePrice'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Breakpoint from 'components/Common/Breakpoint'
import PackageUpgradeSurchargeMarginComparisonText from './PackageUpgradeSurchargeMarginTexts/PackageUpgradeSurchargeMarginComparisonText'
import { connect } from 'react-redux'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import LoyaltyGenericInfoModal from 'loyalty/components/LoyaltyGenericInfoModal'
import ModalContext from 'contexts/ModalContext'
import * as Analytics from 'analytics/analytics'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'

const StyledSlideDown = styled(Expander)`
  &.closed {
    margin-top: 0;
  }
`

const imageProps: ImageParams = {
  mobileAspectRatio: '11:5',
  tabletAspectRatio: '22:15',
  tabletWidth: '200px',
  quality: 'good',
}

interface MappedProps {
  isSpoofed: boolean
  isStoreMode: boolean
  isLoyaltyEnabled: boolean
}

export interface PackageOptionUpgradeView {
  offer: App.Offer
  hasFlightPrices?: boolean
  package: App.Package
  capacityValid?: boolean
  isAvailable?: boolean
  price: number
  memberPrice: number
  priceDifference: number
  memberPriceDifference: number
  isSelected?: boolean
  withFlights?: boolean
  value?: number
  flightPrice?: number
  flightsAvailable?: boolean
}

interface Props {
  offer: App.Offer
  upgradePackageView: App.OfferPackageOptionView
  basePackageView: App.OfferPackageOptionView
  checkIn?: string
  hasFlights?: boolean
}

function PackageUpgradeModal(props: Props & MappedProps) {
  const { offer, upgradePackageView, basePackageView, hasFlights, checkIn, isSpoofed, isStoreMode, isLoyaltyEnabled } = props
  const intl = useIntl()
  const modalContext = useModalElementContext<App.OfferPackageOptionView>()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))

  // Use the same flight price source as FlashHotelOptionPriceDetails.tsx
  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const packageUpgrade = useMemo<PackageOptionUpgradeView>(() => {
    // In charter mode, use addFlightPrice like FlashHotelOptionPriceDetails.tsx
    if (charterBundleRoute && flightPrice) {
      const upgradePricingWithFlights = addFlightPrice(upgradePackageView.pricing, flightPrice)
      const basePricingWithFlights = addFlightPrice(basePackageView.pricing, flightPrice)
      const upgradeMemberPricingWithFlights = upgradePackageView.memberPricing ? addFlightPrice(upgradePackageView.memberPricing, flightPrice) : undefined
      const baseMemberPricingWithFlights = basePackageView.memberPricing ? addFlightPrice(basePackageView.memberPricing, flightPrice) : undefined

      return {
        offer,
        priceDifference: upgradePricingWithFlights.price - basePricingWithFlights.price,
        memberPriceDifference: upgradeMemberPricingWithFlights && baseMemberPricingWithFlights ? upgradeMemberPricingWithFlights.price - baseMemberPricingWithFlights.price : upgradePricingWithFlights.price - basePricingWithFlights.price,
        price: upgradePricingWithFlights.price,
        memberPrice: upgradeMemberPricingWithFlights?.price ?? upgradePricingWithFlights.price,
        package: upgradePackageView.package,
        value: upgradePricingWithFlights.value,
        flightPrice,
        flightsAvailable: upgradePackageView.flightsAvailable,
      }
    }

    // Normal mode (non-charter)
    return {
      offer,
      priceDifference: upgradePackageView.hotelPrice - basePackageView.hotelPrice,
      memberPriceDifference: upgradePackageView.hotelMemberPrice - basePackageView.hotelMemberPrice,
      price: upgradePackageView.hotelPrice,
      memberPrice: upgradePackageView.hotelMemberPrice,
      package: upgradePackageView.package,
      value: upgradePackageView.hotelValue,
      flightPrice: upgradePackageView.flightPrice,
      flightsAvailable: upgradePackageView.flightsAvailable,
    }
  }, [basePackageView, upgradePackageView, offer, charterBundleRoute, flightPrice])

  const onUpgradeClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_ACCEPTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'select_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(upgradePackageView)
  }, [modalContext, trackEvent, upgradePackageView])

  const onNoThanksClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_REJECTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'no_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(basePackageView)
  }, [basePackageView, modalContext, trackEvent])

  const name = packageUpgrade.package.name
  const roomType = packageUpgrade.package.roomType
  const firstRoomImageId = packageUpgrade.package.roomType?.images?.[0]?.id

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(packageUpgrade.package)

  const showLuxPlusInclusions = !!luxPlusInclusions.length

  const [showDetails, setShowDetails] = useState(false)
  const onViewDetails = useCallback(() => {
    setShowDetails(!showDetails)
  }, [showDetails])

  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)

  const basePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: basePackageView,
      currentRegionCode,
      nights: basePackageView?.package?.duration,
    }),
  ], [offer, basePackageView, currentRegionCode])

  const upgradePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: upgradePackageView,
      currentRegionCode,
      nights: upgradePackageView?.package?.duration,
    }),
  ], [offer, upgradePackageView, currentRegionCode])

  const { totalPointsCalculation: basePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(basePackagePointsEarnCalculationRequests)
  const { totalPointsCalculation: upgradePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(upgradePackagePointsEarnCalculationRequests)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)

  const anyPointsEarnFetching = basePackagePointsEarnCalculation.fetching || upgradePackagePointsEarnCalculation.fetching
  const anyPointsEarnError = basePackagePointsEarnCalculation.error || upgradePackagePointsEarnCalculation.error

  const basePackagePoints = basePackagePointsEarnCalculation.data?.points ?? 0
  const basePackageStatusCredits = basePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradePackagePoints = upgradePackagePointsEarnCalculation.data?.points ?? 0
  const upgradePackageStatusCredits = upgradePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradeAdditionalPointsEarn = upgradePackagePoints - basePackagePoints
  const upgradeAdditionalStatusCreditsEarn = upgradePackageStatusCredits - basePackageStatusCredits

  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const baseRoomTypeId = basePackageView.package.roomType?.id
  const upgradeRoomTypeId = upgradePackageView.package.roomType?.id
  const isSameRoomType = useMemo(() => !!baseRoomTypeId && baseRoomTypeId === upgradeRoomTypeId, [baseRoomTypeId, upgradeRoomTypeId])
  const formattedUpgradePrice = useMemo(() => formatCurrency(
    packageUpgrade.memberPriceDifference || packageUpgrade.priceDifference,
    currency,
    currentRegionCode,
    { signDisplay: 'always' },
  ), [currency, currentRegionCode, packageUpgrade.memberPriceDifference, packageUpgrade.priceDifference])

  const title = intl.formatMessage(
    {
      id: '3FKicJsMcZ',
      defaultMessage: 'Upgrade your {upgradeType, select, package {package} other {room}} for only {formattedUpgradePrice} extra',
      description: 'Title for the package/room upgrade modal showing the price difference. upgradeType is "package" when the room type stays the same, or "room" when it changes.',
    },
    { upgradeType: isSameRoomType ? 'package' : 'room', formattedUpgradePrice },
  )
  const subtitle = ''
  const isDismissible = isSpoofed

  const showModal = useContext(ModalContext)
  const loyaltyInfoIconClick = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  const hideMoreDetailsLabel = intl.formatMessage({
    id: '6VnOIDgkY7',
    defaultMessage: 'Hide more details',
    description: 'Toggle button text to hide room details in upgrade modal',
  })
  const viewMoreDetailsLabel = intl.formatMessage({
    id: '8G7cGZG2tq',
    defaultMessage: 'View more details',
    description: 'Toggle button text to view room details in upgrade modal',
  })
  const upgradeButtonLabel = intl.formatMessage({
    id: 'dOSbvmJIkX',
    defaultMessage: 'Upgrade',
    description: 'Button text to confirm the room/package upgrade',
  })
  const noThanksButtonLabel = intl.formatMessage({
    id: '/5s0AXT8hP',
    defaultMessage: 'No thanks',
    description: 'Button text to decline the room/package upgrade',
  })

  return (
    <ModalBase size="L" height="full" tabletHeight="auto" dismissible={isDismissible}>
      <ModalHeader
        dismissible={isDismissible}
        title={title}
        subtitle={subtitle}
      />
      <ModalBody>
        <Breakpoint max="mobile">
          <div>
            {!!roomType?.images.length && <ImageGallery
              images={roomType?.images}
              imageParams={imageProps}
            />}
          </div>
        </Breakpoint>
        <ModalContent>
          <Group direction="vertical" gap={12}>
            {isSpoofed && !isStoreMode && <PackageUpgradeSurchargeMarginComparisonText basePackage={basePackageView.package} upgradePackage={upgradePackageView.package} checkIn={checkIn} />}
            <VerticalSpacer gap={12}>
              <CSSBreakpoint min="tablet">
                <VerticalSpacer gap={12}>
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                    <VerticalSpacer gap={12}>
                      <Heading variant="heading4">{name}</Heading>
                      {roomType?.sizeSqm && <BodyText variant="small">
                        <FormattedMessage
                          id="vrraA51gEK"
                          defaultMessage="<b>Room size:</b> {sizeSqm}m²"
                          description="Room size with label in the upgrade modal (tablet)"
                          values={{
                            sizeSqm: roomType.sizeSqm,
                            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                          }}
                        />
                      </BodyText>}
                      <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                      {isCurrentRegionUserLoyaltyRegion && <>
                        {anyPointsEarnFetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size="S" />}
                        {!anyPointsEarnError && !anyPointsEarnFetching && upgradeAdditionalPointsEarn > 0 && <PriceRowLoyaltyPoints
                          tier={displayTier}
                          earnablePoints={upgradeAdditionalPointsEarn}
                          statusCredits={upgradeAdditionalStatusCreditsEarn}
                          size="S"
                          calculationType="additional"
                          onInfoIconClick={loyaltyInfoIconClick}
                        />}
                      </>}
                    </VerticalSpacer>
                    {firstRoomImageId && <ResponsiveImage
                      {...imageProps}
                      id={firstRoomImageId}
                      alt={`${roomType?.name} image 1 at ${offer.property?.name} by ${offer.property?.geoData.administrativeAreaLevel2}, ${offer.property?.geoData.administrativeAreaLevel1}, ${offer.property?.geoData.country}`}
                    />}
                  </Group>
                </VerticalSpacer>
              </CSSBreakpoint>
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={12}>
                  <Heading variant="heading4">{name}</Heading>
                  <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                  {isLoyaltyEnabled && <>
                    {anyPointsEarnFetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size="S" />}
                    {!anyPointsEarnError && !anyPointsEarnFetching && upgradeAdditionalPointsEarn > 0 && <PriceRowLoyaltyPoints
                      tier={displayTier}
                      earnablePoints={upgradeAdditionalPointsEarn}
                      statusCredits={upgradeAdditionalStatusCreditsEarn}
                      size="S"
                      calculationType="additional"
                      onInfoIconClick={loyaltyInfoIconClick}
                    />}
                  </>}
                  {roomType?.sizeSqm && <BodyText variant="small">
                    <FormattedMessage
                      id="vrraA51gEK"
                      defaultMessage="<b>Room size:</b> {sizeSqm}m²"
                      description="Room size with label in the upgrade modal (tablet)"
                      values={{
                        sizeSqm: roomType.sizeSqm,
                        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                      }}
                    />
                  </BodyText>}
                </VerticalSpacer>
              </CSSBreakpoint>
            </VerticalSpacer>
            <CSSBreakpoint min="tablet">
              <Divider kind="primary" />
            </CSSBreakpoint>

            <VerticalSpacer gap={16}>
              {showLuxPlusInclusions && <LuxPlusInclusions
                luxPlusInclusions={luxPlusInclusions}
                accommodationOffer={offer}
              />}
              {(!!inclusions.length || !!bonusInclusions.length) && <Group direction="vertical" gap={12}>
                <BodyText variant="small" weight="bold">
                  {intl.formatMessage(
                    {
                      id: '62/B/n4yni',
                      defaultMessage: '{count, plural, one {# inclusion} other {# inclusions}}:',
                      description: 'Inclusions count label in the package upgrade modal',
                    },
                    { count: inclusions.length + bonusInclusions.length },
                  )}
                </BodyText>
                <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} size="small" />
              </Group>}
              {roomType && <StyledSlideDown expanded={showDetails} className={showDetails ? '' : 'closed'}>
                <LePackageUpgradeCardRoomDetails roomType={roomType} />
              </StyledSlideDown>}
              <SmallToggleButton size="caption" on={showDetails} onClick={onViewDetails}>
                {showDetails ? hideMoreDetailsLabel : viewMoreDetailsLabel}
              </SmallToggleButton>
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          onClick: onUpgradeClick,
          children: upgradeButtonLabel,
        }}
        secondaryActionProps={{
          onClick: onNoThanksClick,
          children: noThanksButtonLabel,
        }}
      />
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return {
    isSpoofed: state.auth.account.isSpoofed,
    isStoreMode: state.system.storeMode,
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
  }
}

export default connect(mapStateToProps)(PackageUpgradeModal)
